import { useState, useEffect } from 'react';
import { getOrders, updateOrder, Order } from '../api/client';
import ShopNav from '../components/ShopNav';
import './Orders.css';
import { overlayClose } from '../utils/overlayClose';

const ORDER_STATUSES = ['received', 'packed', 'shipped', 'delivered', 'cancelled'] as const;

const STATUS_LABELS: Record<string, string> = {
  received: 'Received',
  packed: 'Ready to Collect',
  shipped: 'Collected',
  delivered: 'Complete',
  cancelled: 'Cancelled',
};

function Orders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setIsLoading(true);
      const data = await getOrders();
      setOrders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedOrder(null);
  };

  const handleUpdateStatus = async (orderId: string, status: typeof ORDER_STATUSES[number]) => {
    try {
      setError('');
      setSuccessMessage('');
      await updateOrder(orderId, { status });
      setSuccessMessage(`Order status updated to ${getStatusLabel(status)}`);
      await loadOrders();

      if (selectedOrder?.id === orderId) {
        const updatedOrder = orders.find(o => o.id === orderId);
        if (updatedOrder) {
          setSelectedOrder({ ...updatedOrder, status });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update order status');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const getStatusLabel = (status: string) => STATUS_LABELS[status] || status;

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'received':
        return 'badge-status-received';
      case 'packed':
        return 'badge-status-ready';
      case 'shipped':
        return 'badge-status-collected';
      case 'delivered':
        return 'badge-status-complete';
      case 'cancelled':
        return 'badge-danger';
      default:
        return 'badge-status-received';
    }
  };

  if (isLoading) {
    return <div className="loading">Loading orders...</div>;
  }

  return (
    <div className="orders-page">
      <ShopNav />
      <div className="page-header">
        <div>
          <h1 className="page-title">Orders</h1>
          <p className="page-subtitle">Manage customer orders</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Desktop table */}
      <div className="card orders-table-wrapper">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td>
                    <span className="order-id">#{order.id.slice(0, 8)}</span>
                  </td>
                  <td>
                    <div>
                      <div className="customer-name">{order.customerName}</div>
                      <div className="customer-email">{order.customerEmail}</div>
                    </div>
                  </td>
                  <td>
                    <span className="items-count">{order.items.length} item(s)</span>
                  </td>
                  <td className="order-total">{formatCurrency(order.total)}</td>
                  <td>
                    <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </span>
                  </td>
                  <td className="order-date">{formatDate(order.createdAt)}</td>
                  <td>
                    <button
                      onClick={() => handleViewOrder(order)}
                      className="btn btn-secondary btn-small"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="orders-cards">
        {orders.map((order) => (
          <div key={order.id} className="order-card" onClick={() => handleViewOrder(order)}>
            <div className="order-card-header">
              <span className="order-id">#{order.id.slice(0, 8)}</span>
              <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                {getStatusLabel(order.status)}
              </span>
            </div>
            <div className="order-card-body">
              <div className="order-card-customer">{order.customerName}</div>
              <div className="order-card-meta">
                <span>{order.items.length} item(s)</span>
                <span className="order-card-total">{formatCurrency(order.total)}</span>
              </div>
              <div className="order-card-date">{formatDate(order.createdAt)}</div>
            </div>
          </div>
        ))}
      </div>

      {orders.length === 0 && (
        <div className="empty-state">
          <p>No orders yet.</p>
        </div>
      )}

      {showModal && selectedOrder && (
        <div className="modal-overlay" {...overlayClose(handleCloseModal)}>
          <div className="modal-content order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Order Details</h2>
              <button onClick={handleCloseModal} className="modal-close">
                &times;
              </button>
            </div>

            <div className="order-details">
              <div className="detail-section">
                <h3 className="detail-heading">Order Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Order ID:</span>
                    <span className="detail-value">#{selectedOrder.id}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Status:</span>
                    <span className={`badge ${getStatusBadgeClass(selectedOrder.status)}`}>
                      {getStatusLabel(selectedOrder.status)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Date:</span>
                    <span className="detail-value">{formatDate(selectedOrder.createdAt)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Total:</span>
                    <span className="detail-value order-total">{formatCurrency(selectedOrder.total)}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3 className="detail-heading">Customer Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">{selectedOrder.customerName}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{selectedOrder.customerEmail}</span>
                  </div>
                  {selectedOrder.customerPhone && (
                    <div className="detail-item">
                      <span className="detail-label">Phone:</span>
                      <span className="detail-value">{selectedOrder.customerPhone}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="detail-section">
                <h3 className="detail-heading">Collection</h3>
                <p className="collection-note">Customer will collect at next training session or match day.</p>
              </div>

              <div className="detail-section">
                <h3 className="detail-heading">Order Items</h3>
                <div className="order-items">
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} className="order-item">
                      <div className="item-info">
                        <span className="item-name">{item.productName}</span>
                        <span className="item-details">
                          {item.size ? `Size: ${item.size} | ` : ''}Qty: {item.quantity}
                        </span>
                      </div>
                      <div className="item-price">
                        {formatCurrency(item.price * item.quantity)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="detail-section">
                <h3 className="detail-heading">Update Status</h3>
                <div className="status-actions">
                  {ORDER_STATUSES.filter(s => s !== 'cancelled').map((status) => (
                    <button
                      key={status}
                      onClick={() => handleUpdateStatus(selectedOrder.id, status)}
                      className={`btn btn-small ${
                        selectedOrder.status === status ? 'btn-primary' : 'btn-outline'
                      }`}
                      disabled={selectedOrder.status === status}
                    >
                      {getStatusLabel(status)}
                    </button>
                  ))}
                  <button
                    onClick={() => handleUpdateStatus(selectedOrder.id, 'cancelled')}
                    className="btn btn-danger btn-small"
                    disabled={selectedOrder.status === 'cancelled' || selectedOrder.status === 'delivered'}
                  >
                    Cancel Order
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Orders;
